import { useCallback, useEffect, useMemo, type MutableRefObject } from 'react';

import type { HeliosRecordDataTablesColumn, HeliosRecordDataTablesReq } from 'types/dataHelios';
import type { HeliosUserFilter } from 'types/heliosUserFilters';
import type { FilterCondition } from 'types/tableFilters';
import { toHeliosApiFilters } from 'utils/helios/toApiFilters';

import { useHeliosFilters } from './useHeliosFilters';

interface UseHeliosBrowseFiltersProps {
  templateId: string;
  open: boolean;
  columnsRef: MutableRefObject<HeliosRecordDataTablesColumn[] | undefined>;
  setPagination: (pagination: { page: number; pageSize: number }) => void;
  pageSize: number;
}

const useHeliosBrowseFilters = ({ templateId, open, columnsRef, setPagination, pageSize }: UseHeliosBrowseFiltersProps) => {
  const {
    activeFilters,
    setActiveFilters,
    activeSavedFilterId,
    savedFilters,
    isSavingOrUpdating,
    handleSaveFilter,
    handleUpdateFilter,
    handleDeleteSavedFilter,
    handleLoadFilter,
    handleUnloadFilter,
    handleApplyFilters,
  } = useHeliosFilters({
    templateViewId: templateId,
    setPagination,
    pageSize,
    selectDefaultSavedFilter: false,
  });

  const apiFilters: HeliosRecordDataTablesReq['filters'] = useMemo(
    () => toHeliosApiFilters(activeFilters, columnsRef.current),
    [activeFilters, columnsRef]
  );

  useEffect(() => {
    if (open) {
      return;
    }

    setActiveFilters([]);
    handleUnloadFilter();
  }, [open, setActiveFilters, handleUnloadFilter]);

  const handleSaveBrowseFilter = useCallback(
    (name: string, isDefault: boolean) => handleSaveFilter(name, isDefault, columnsRef.current),
    [columnsRef, handleSaveFilter]
  );

  const handleUpdateBrowseFilter = useCallback(
    (name: string, isDefault: boolean, explicitConditions?: FilterCondition[]) =>
      handleUpdateFilter(name, isDefault, columnsRef.current, explicitConditions),
    [columnsRef, handleUpdateFilter]
  );

  const handleLoadBrowseFilter = useCallback(
    (filter: HeliosUserFilter) => handleLoadFilter(filter, columnsRef.current),
    [columnsRef, handleLoadFilter]
  );

  return {
    apiFilters,
    activeFilters,
    savedFilters,
    activeSavedFilterId,
    isSavingOrUpdating,
    handleApplyFilters,
    handleSaveFilter: handleSaveBrowseFilter,
    handleUpdateFilter: handleUpdateBrowseFilter,
    handleDeleteSavedFilter,
    handleLoadFilter: handleLoadBrowseFilter,
    handleUnloadFilter,
  };
};

export { useHeliosBrowseFilters };
